import {dismiss} from "@khan/component-dismissible";
import {useGql, type GqlFetchFn} from "@khan/data-gql";
import {useGuideCommandListener} from "@khan/events-guide";
import {GuideStateApi} from "@khan/guide-state";
import type {Message} from "@khan/guide-types";
import Log, {Errors} from "@khan/logging";

import {
    PopoverClosedReason,
    type Notification,
    type NotificationStyle,
} from "../types.ts";

import type {NotificationAction} from "./reducer.ts";

export interface NotificationStreamData {
    entry: Message;
    metadata: {
        dismissKey?: string;
        navigationId: string;
        style: NotificationStyle;
        threadId: string;
        trigger: string;
    };
}

interface Props {
    dispatch: React.Dispatch<NotificationAction>;
}

/**
 * Listens for notification events from the server and dispatches them to the
 * manager to be rendered.
 */
export const NotificationStreamListener = (props: Props): React.ReactNode => {
    const {dispatch} = props;
    const gqlFetch = useGql();

    useGuideCommandListener(({name, content: response}) => {
        if (name !== "notification/dispatch-response") {
            return;
        }
        // At this time, there are no notifications we want to handle while
        // KM is offline. If we receive a notification event while KM is
        // uavailable, we drop the event and do nothing.
        if (GuideStateApi.latest().isOffline) {
            return;
        }
        // We don't display notifications if the sidebar is open.
        if (GuideStateApi.latest().open) {
            return;
        }

        try {
            const json: NotificationStreamData = JSON.parse(response);
            // If the notification is for another navigation session, ignore it
            if (
                json.metadata.navigationId !==
                GuideStateApi.latest().navigation.id
            ) {
                return;
            }

            const notification = mapToNotification(json, dispatch, gqlFetch);

            if (!notification) {
                return;
            }

            dispatch({
                type: "SHOW",
                notification,
                style: json.metadata.style,
                eventName: json.metadata.trigger,
            });
        } catch (error: any) {
            Log.error("Error parsing response", Errors.Internal, {
                cause: error,
                metadata: {response},
            });
        }
    });
    return null;
};

// Exported for tests
// eslint-disable-next-line react-refresh/only-export-components -- porting over from old code.
export const mapToNotification = (
    data: NotificationStreamData,
    dispatch: React.Dispatch<NotificationAction>,
    gqlFetch: GqlFetchFn,
): Notification | null => {
    const {
        entry,
        metadata: {dismissKey, style},
    } = data;
    switch (style) {
        case "action-bubbles":
        case "message":
            return {
                ...entry,
                onClose: (reason: PopoverClosedReason) => {
                    dispatch({type: "HIDE"});
                    // If a non-empty dismissKey is attached to the
                    // notification, then we'll apply some custom dismissal
                    // behavior.
                    // NOTE: If a more customized dismissal behavior is needed,
                    // consider conditioning on metadata.trigger.
                    if (dismissKey) {
                        switch (reason) {
                            case PopoverClosedReason.userDismissed:
                                dismiss(gqlFetch, dismissKey, 30);
                                break;
                            case PopoverClosedReason.ctaClicked:
                                dismiss(gqlFetch, dismissKey, 7);
                                break;
                            case PopoverClosedReason.autoDismissed:
                                dismiss(gqlFetch, dismissKey, 1);
                                break;
                        }
                    }
                },
            };
        default:
            const _: never = style;
            Log.log("Unknown notification style", {style});
            return null;
    }
};
